"use client"

import { useCallback, useEffect, useRef } from "react"
import { CuratedProductPicksCard, type CuratedProductRoleTone } from "@/components/analysis/curated-product-picks-card"
import type { ConditionType, ResolvedConditionPick } from "@/data/condition-product-picks"
import { getProductByKey } from "@/data/guide-product-recommendations"
import { usePathname } from "next/navigation"
import { calculatorTypeProps, contentAnalytics, trackEvent } from "@/lib/analytics"
import { resolveCalculatorType } from "@/lib/calculator-tool-types"
import { withAmazonTagForPlacement } from "@/lib/affiliate"
import type { DiagnosisIdentification } from "@/lib/types/diagnosis"

const ROLE_LABELS: Record<ResolvedConditionPick['role'], string> = {
  curative: 'Curative',
  preventive: 'Preventive',
  recovery: 'Recovery',
  tool: 'Tool',
}

const ROLE_TONES: Record<ResolvedConditionPick['role'], CuratedProductRoleTone> = {
  curative: 'danger',
  preventive: 'info',
  recovery: 'success',
  tool: 'warning',
}

const ASIN_PATTERNS = [
  /\/dp\/([A-Z0-9]{10})(?:[/?]|$)/i,
  /\/gp\/product\/([A-Z0-9]{10})(?:[/?]|$)/i,
  /[?&]asin=([A-Z0-9]{10})(?:&|$)/i,
]

function extractAsin(url: string): string | undefined {
  for (const pattern of ASIN_PATTERNS) {
    const match = url.match(pattern)
    if (match) return match[1].toUpperCase()
  }
  return undefined
}

const DIAGNOSIS_PICKS_PLACEMENT = 'diagnosis-result'

function taggedHref(pick: ResolvedConditionPick): string {
  return withAmazonTagForPlacement(pick.product.affiliateUrl, DIAGNOSIS_PICKS_PLACEMENT)
}

interface DiagnosisProductPicksProps {
  problem: DiagnosisIdentification
  conditionSlug: string
  conditionType: ConditionType
  picks: ResolvedConditionPick[]
  hostGrassVerified?: boolean
  title?: string
  description?: string
  className?: string
}

export function DiagnosisProductPicks({
  problem,
  conditionSlug,
  conditionType,
  picks,
  hostGrassVerified = false,
  title,
  description,
  className,
}: DiagnosisProductPicksProps) {
  const shownKeyRef = useRef<string | null>(null)
  const shelfRef = useRef<HTMLElement | null>(null)
  const unifiedShownKeyRef = useRef<string | null>(null)
  // Same source `calculator_used` reads. The shelf also renders off /tools
  // (homepage and result diagnosis), where the surface itself is the only
  // calculator identity there is, so it stands in.
  const pathname = usePathname()
  const calculatorType = resolveCalculatorType(pathname) ?? 'diagnosis-result'

  useEffect(() => {
    if (picks.length === 0) return
    const shownKey = `${conditionSlug}:${picks.map((pick) => pick.productKey).join('|')}`
    if (shownKeyRef.current === shownKey) return
    shownKeyRef.current = shownKey

    picks.forEach((pick, index) => {
      trackEvent('diagnosis_products_shown', {
        condition_slug: conditionSlug,
        condition_type: conditionType,
        severity: problem.severity,
        pick_source: 'curated',
        pick_role: pick.role,
        position: index + 1,
        product_name: pick.product.name,
        product_category: pick.product.category,
        asin: extractAsin(pick.product.affiliateUrl),
      })
    })
  }, [conditionSlug, conditionType, picks, problem.severity])

  // Unified affiliate impression, ADDITIVE to the mount-timed
  // `diagnosis_products_shown` above. That one answers "was the shelf
  // rendered?"; this one is viewport-gated at 50% so it pairs with
  // `affiliate_product_clicked` and makes diagnosis-result CTR computable on the
  // same terms as every other affiliate surface. Same fire-once-per-pick-set key
  // as the bespoke event so the two stay joinable. Pattern mirrors the weed
  // shelf (components/weed-identifier/weed-result-presentation.tsx).
  useEffect(() => {
    const node = shelfRef.current
    if (!node || picks.length === 0) return
    const shownKey = `${conditionSlug}:${picks.map((pick) => pick.productKey).join('|')}`
    if (unifiedShownKeyRef.current === shownKey) return

    const observer = new IntersectionObserver(([visible]) => {
      if (!visible.isIntersecting || unifiedShownKeyRef.current === shownKey) return
      unifiedShownKeyRef.current = shownKey

      picks.forEach((pick, index) => {
        contentAnalytics.affiliateProductShown('diagnosis-result', {
          placement: 'diagnosis-result',
          condition_slug: conditionSlug,
          condition_type: conditionType,
          severity: problem.severity,
          pick_source: 'curated',
          pick_role: pick.role,
          position: index + 1,
          product_name: pick.product.name,
          product_category: pick.product.category,
          asin: extractAsin(pick.product.affiliateUrl),
        })
      })
      observer.disconnect()
    }, { threshold: 0.5 })

    observer.observe(node)
    return () => observer.disconnect()
  }, [conditionSlug, conditionType, picks, problem.severity])

  const handleProductClick = useCallback((pick: ResolvedConditionPick, index: number) => {
    trackEvent('calculator_product_clicked', {
      condition_slug: conditionSlug,
      condition_type: conditionType,
      severity: problem.severity,
      pick_source: 'curated',
      pick_role: pick.role,
      position: index + 1,
      product_name: pick.product.name,
      product_category: pick.product.category,
      asin: extractAsin(pick.product.affiliateUrl),
      tracking_context: 'diagnosis-result',
      ...calculatorTypeProps(calculatorType),
    })
    // Unified click on the SAME surface as the impression above, carrying the
    // SAME segmentation dimensions — a property present on shown but absent on
    // clicked silently zeroes the click numerator of any CTR split on it.
    contentAnalytics.affiliateProductClicked('diagnosis-result', {
      productName: pick.product.name,
      affiliateLink: taggedHref(pick),
      asin: extractAsin(pick.product.affiliateUrl),
      placement: 'diagnosis-result',
      condition_slug: conditionSlug,
      condition_type: conditionType,
      severity: problem.severity,
      pick_source: 'curated',
      position: index + 1,
      pick_role: pick.role,
      product_category: pick.product.category,
    })
  }, [conditionSlug, conditionType, problem.severity, calculatorType])

  if (picks.length === 0) return null

  const items = picks.map((pick) => ({
    id: pick.productKey,
    name: pick.product.name,
    note: pick.note,
    imageUrl: pick.product.imageUrl,
    affiliateUrl: taggedHref(pick),
    roleLabel: ROLE_LABELS[pick.role],
    roleTone: ROLE_TONES[pick.role],
  }))

  return <CuratedProductPicksCard
    ref={shelfRef}
    headingId="diagnosis-product-picks-heading"
    title={title ?? `Best treatments for ${problem.name}`}
    description={description ?? (hostGrassVerified
      ? 'Matched to this diagnosis, severity, season, and your confirmed grass.'
      : 'Matched to this diagnosis and season. Check each treatment label for your grass and conditions.')}
    items={items}
    onProductClick={(_item, index) => {
      const pick = picks[index]
      if (pick) handleProductClick(pick, index)
    }}
    className={className}
  />
}

const HEALTHY_PRODUCT_DEFINITIONS = [
  {
    productKey: 'smart-soil-sensor' as const,
    role: 'tool' as const,
    note: 'Check root-zone moisture before watering so healthy turf does not drift into drought or overwatering stress.',
  },
  {
    productKey: 'fertilizer-simple-1648' as const,
    role: 'recovery' as const,
    note: 'A measured balanced feed for the lawn’s normal growing-season schedule when feeding is due.',
  },
  {
    productKey: 'tool-spreader-edgeguard-mini' as const,
    role: 'tool' as const,
    note: 'Keep fertilizer and prevention applications even and off driveways and garden beds.',
  },
]

const HEALTHY_PRODUCT_PICKS: ResolvedConditionPick[] = HEALTHY_PRODUCT_DEFINITIONS.flatMap((pick) => {
  const product = getProductByKey(pick.productKey)
  return product ? [{ ...pick, product }] : []
})

const HEALTHY_FINDING: DiagnosisIdentification = {
  type: 'environmental',
  name: 'healthy lawn maintenance',
  confidence: 100,
  severity: 'mild',
  urgency: 'monitor',
  visualEvidence: [],
}

export function HealthyDiagnosisProductPicks() {
  return (
    <DiagnosisProductPicks
      problem={HEALTHY_FINDING}
      conditionSlug="healthy-maintenance"
      conditionType="environmental"
      picks={HEALTHY_PRODUCT_PICKS}
      hostGrassVerified={false}
      title="Keep-it-healthy essentials"
      description="Three practical picks for monitoring, feeding when due, and applying lawn products evenly."
    />
  )
}
